"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import { Desktop, DesktopOnly, LargeDesktop, Mobile } from "ui"
import Container from "ui/Container"
import { sharedColors } from "ui/theme/colors"
import Form from "./Form"
import { FormObject } from "./Hero"

const GeneralLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-anchor: none;
`

const StyledLayout = styled.div`
  width: 100%;
  background-color: ${sharedColors.brandDark};
  padding: 24px 0;
  position: relative;
  height: 255px;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 784px;
    min-height: 600px;

    padding: 64px 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    min-height: 624px;
    height: 831px;
    padding: 88px 0;
  }
`

const MobileFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${sharedColors.brandDark};
  padding: 20px 16px 40px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: none;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  height: 207px;
  z-index: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    position: absolute;
    height: 600px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    height: 624px;
  }
`

const BusImage = styled(Image, {
  shouldForwardProp: (prop) => prop !== "$isInverted",
})<{ $isInverted: boolean }>`
  z-index: 1;
  bottom: 0;
  left: -25%;
  position: absolute;
  transform: translateX(-100%)
    scaleX(${(props) => (props.$isInverted ? -1 : 1)});
  /* Slower and smoother bus entrance */
  animation: slideBusIn 2.2s cubic-bezier(0.77, 0, 0.175, 1) forwards;
  animation-delay: 0.3s;
  ${(props) => props.theme.breakpoints.up("md")} {
    bottom: 7%;
    left: -20%;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    left: -5%;
    bottom: 12%;
  }

  @keyframes slideBusIn {
    from {
      transform: translateX(-100%)
        scaleX(${(props) => (props.$isInverted ? -1 : 1)});
    }
    to {
      transform: translateX(0)
        scaleX(${(props) => (props.$isInverted ? -1 : 1)});
    }
  }
`

export default function AnimationHero({
  formObject,
  locale,
}: {
  formObject: FormObject
  locale: string
}) {
  const isInverted = locale === "en" || locale === "ru"
  return (
    <GeneralLayout>
      <StyledLayout>
        <Desktop>
          <Container>
            <Form formObject={formObject} platform="desktop" />
          </Container>
        </Desktop>

        <ImageContainer>
          <Mobile>
            <BusImage
              src={"/images/mini-bus.png"}
              width={346}
              height={133}
              alt="hero"
              priority
              $isInverted={isInverted}
            />
            <Image
              src={"/images/herosm.svg"}
              fill
              alt="hero"
              style={{
                objectFit: "cover",
                objectPosition: "bottom",
                transform: isInverted ? "scaleX(-1)" : "scaleX(1)",
              }}
              priority
            />
          </Mobile>
          <DesktopOnly>
            <BusImage
              src={"/images/busmd.svg"}
              width={896}
              height={346}
              alt="hero"
              priority
              $isInverted={isInverted}
            />
            <Image
              src={"/images/heromd.svg"}
              fill
              alt="hero"
              style={{
                objectFit: "cover",
                objectPosition: "bottom",
                transform: isInverted ? "scaleX(-1)" : "scaleX(1)",
              }}
              priority
            />
          </DesktopOnly>
          <LargeDesktop>
            <BusImage
              src={"/images/busxl.svg"}
              width={1065}
              height={411}
              alt="hero"
              priority
              $isInverted={isInverted}
            />
            <Image
              src={"/images/heroxl.svg"}
              fill
              alt="hero"
              style={{
                objectFit: "cover",
                objectPosition: "bottom",
                transform: isInverted ? "scaleX(-1)" : "scaleX(1)",
              }}
              priority
            />
          </LargeDesktop>
        </ImageContainer>
      </StyledLayout>
      <MobileFormContainer>
        <Form formObject={formObject} platform="mobile" />
      </MobileFormContainer>
    </GeneralLayout>
  )
}
